import { AdjustmentType } from "@redotech/congo-enums/adjustment-type";
import { ReturnProductStrategy } from "@redotech/congo-enums/return-product-strategy";
import { Currency } from "@redotech/money/currencies";
import { MoneyValue } from "@redotech/money/money-calculator";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AllocationRecord } from "../allocation-record";
import {
  NewOrderDiscountKind,
  NewOrderDiscountKinds,
} from "../new-order-discount-details";

export interface ProductTotals<C extends Currency> {
  id: string;
  type: ReturnProductStrategy;
  /**
   * The return value for the product. This is the value that will be used in other return calculations,
   * all other product totals are used to calculate this value
   */
  returnValue: { amount: MoneyValue<C> };
  /** The new order value for the product. Will be zero unless the product has a variant exchange/exchange group */
  newOrderVariantValue: MoneyValue<C>;
  /** The adjustments applied to the product, including return flow and merchant manual adjustments */
  adjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
  originalValue: {
    /**
     * The discount for the product (generally just the line item discount divided by quantity, but can be influenced by
     * discount distribution, bundles, and returns of exchanges)
     */
    discount: MoneyValue<C>;
    /** The per-unit markdown from the variant's order-time compare-at price; zero or absent when none */
    compareAtDiscount?: MoneyValue<C>;
    /** The price after the discount has been applied */
    discountedPrice: MoneyValue<C>;
    price: MoneyValue<C>;
    tax: MoneyValue<C>;
    duty: MoneyValue<C>;
  };
}

export interface ReturnTotals<C extends Currency> {
  returnValue: {
    /** The total return value of all products being returned */
    amount: MoneyValue<C>;
  };
  /** A list of totals associated with each product, including the return value and all the values that were used to calculate it */
  productTotalsList: ProductTotals<C>[];
  /** The amount of store credit that will be issued to the customer */
  storeCredit: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /** The amount of refund that will be issued to the customer */
  refund: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /**
   * The amount of outstanding balance the customer owes, broken out by
   * whether the draft-billed exchange shipping fee is folded in.
   *
   * - `withoutDraftBilledShipping` — the pure return-time settle-up
   *   amount (charge minus refunds/credits, items + return-shipping
   *   only). Use for surfaces that itemize the exchange shipping line
   *   separately (e.g. a New Order breakdown that already shows the
   *   shipping line).
   * - `withDraftBilledShipping` — `withoutDraftBilledShipping` plus the
   *   exchange shipping fee when it's billed via the draft order
   *   (`chargesExchangeShippingViaStripe === false`); equal to
   *   `withoutDraftBilledShipping` otherwise. Use for surfaces showing
   *   a single aggregate "what you owe".
   *
   * Mirrors the `storeCredit.{with,without}OriginalOrderShipping` and
   * `refund.{with,without}OriginalOrderShipping` pattern so consumers
   * can pick the right total without re-deriving the billing path.
   */
  outstandingBalanceTotals: {
    withDraftBilledShipping: MoneyValue<C>;
    withoutDraftBilledShipping: MoneyValue<C>;
    /** Balance dropped for being under the minimum charge; zero otherwise. */
    waivedBelowMinimum?: MoneyValue<C>;
  };
  /**
   * @deprecated Use {@link outstandingBalanceTotals} instead. Equals
   * `outstandingBalanceTotals.withDraftBilledShipping`. Kept on the
   * output for one deploy cycle so frontend builds running ahead of (or
   * behind) the backend during a rolling deploy still resolve a value;
   * removed in a follow-up PR after both sides are caught up.
   */
  outstandingBalance: MoneyValue<C>;
  newOrderTotals: {
    /** The total price of all exchange items (post discounts and taxes) */
    totalValue: MoneyValue<C>;
    exchangeShippingFee: MoneyValue<C>;
    /** Duties and taxes the shopper pays with a duties-prepaid exchange rate. Never folded into `exchangeShippingFee`. */
    exchangeLandedCost: MoneyValue<C>;
  };
  /** Every posting the engine made and every account it worked from. Present once the return carries a version stamp. */
  allocation?: AllocationRecord;
  adjustmentTotals: {
    /** Return level adjustments, such as finalize flow and customer label upcharge fee */
    returnLevelAdjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
    unusedUseItOrLoseItBonus: MoneyValue<C>;
  };
  /**
   * For returns with usePreDiscountPrice enabled, this is the sum of discounts across all return products
   * that will be applied as a use-it-or-lose-it shop now bonus
   */
  preDiscountShopNowBonus: {
    total: MoneyValue<C>;
    unused: MoneyValue<C>;
    used: MoneyValue<C>;
  };
  finalSaleCredit: MoneyValue<C>;
  returnShippingFee: {
    /** The shopper-owed shipping fee, deducted as one amount. */
    totalAmount: MoneyValue<C>;
    /** The label fee part; zero when Redo or the merchant pays the label. Optional like `fromExchange`. */
    labelFeeComponent?: MoneyValue<C>;
    /** The selected carrier's price above the covered quote. Optional like `fromExchange`. */
    carrierUpgradeFeeComponent?: MoneyValue<C>;
    amountDeducted: {
      fromStoreCredit: MoneyValue<C>;
      fromRefund: MoneyValue<C>;
      // Optional for backwards compatibility — older serialized totals
      // (persisted snapshots, in-flight RPC payloads predating the
      // exchange-bucket deduction) won't have this field. Readers must
      // treat `undefined` as zero.
      fromExchange?: MoneyValue<C>;
    };
  };
}

export interface ExchangeItemTotals<C extends Currency> {
  /** `ProductTotals.id` for variant exchanges; the advanced exchange item id for advanced ones. */
  id: string;
  price: MoneyValue<C>;
  discount: MoneyValue<C>;
  discountedPrice: MoneyValue<C>;
  /**
   * This item's share of the new order taxes. Sums to
   * `newOrderTotals.taxes.total` across the list.
   */
  tax: MoneyValue<C>;
  /**
   * Named per-item discount lines when the source persisted attribution
   * (annotation only — `discount` remains the arithmetic source of truth).
   */
  discountLines?: {
    label: string;
    kind: NewOrderDiscountKind;
    amount: MoneyValue<C>;
  }[];
  /**
   * The returned units this line settled against, unit for unit, when the
   * allocation engine paired them: one key per settled unit, so a line of two
   * with one settled lists one. A settled unit is valued at the unit it
   * settled against and owes nothing for itself, as a variant exchange already
   * does; the rest of the line is priced and owed as bought.
   */
  settledBy?: readonly string[];
  /**
   * This item's share of what the customer owes. Only sums to the outstanding
   * balance when advanced exchange items are present to absorb the remainder.
   */
  distributedUpsellValue: MoneyValue<C>;
}

export interface ProductSummaryTotals<
  C extends Currency,
> extends ProductTotals<C> {
  returnValue: ProductTotals<C>["returnValue"] & { preTax: MoneyValue<C> };
}

export interface ReturnSummaryTotals<C extends Currency> extends Omit<
  ReturnTotals<C>,
  "returnData"
> {
  presentmentCurrency: C;
  shopCurrency: Currency;
  returnValue: ReturnTotals<C>["returnValue"] & {
    preTaxPreAdjustments: MoneyValue<C>;
    preAdjustments: MoneyValue<C>;
    preReturnLevelAdjustments?: MoneyValue<C>;
    totalTaxes: MoneyValue<C>;
    totalDiscount: MoneyValue<C>;
    totalDuty: MoneyValue<C>;
    /**
     * Total duty (price + tax-on-duty) on returned line items eligible for a
     * merchant invoice credit (duty drawback). Only populated when the team
     * has duty drawback enabled and the return shipping country is in the
     * configured eligibility list. This is independent of `totalDuty`, which
     * tracks duties refunded to the customer.
     */
    merchantDutyDrawback: MoneyValue<C>;
  };
  totalPaymentOwed: MoneyValue<C>;
  /**
   * Net cash movement on the return, signed from the customer's perspective:
   * positive when the customer pays (upsell exchange), negative when the
   * customer is paid out (refund, or a downsell exchange settled as a
   * refund), zero for an even exchange. Store credit is excluded — it isn't
   * cash, so a store-credit return nets zero.
   *
   * - `withoutShipping` — merchandise only. Every shipping amount is stripped
   *   out: the exchange shipping fee, the return label fee (however it was
   *   collected), and any original order shipping refunded.
   * - `withShipping` — every dollar that moves, shipping included.
   */
  netReturnAmount: {
    withoutShipping: MoneyValue<C>;
    withShipping: MoneyValue<C>;
  };
  newOrderTotals: ReturnTotals<C>["newOrderTotals"] & {
    preTax: MoneyValue<C>;
    exchangeItemTotalsList: ExchangeItemTotals<C>[];
    totalDiscount: MoneyValue<C>;
    taxes: {
      forVariantExchangeProducts: MoneyValue<C>;
      forAdvancedExchangeProducts: MoneyValue<C>;
      total: MoneyValue<C>;
    };
  };
  adjustmentTotals: ReturnTotals<C>["adjustmentTotals"] & {
    /** The net adjustment value after adding up all return and product level adjustments */
    totalNetAdjustmentValue: MoneyValue<C>;
    /** The net merchant manual adjustment value after adding up all merchant manual adjustments */
    totalNetMerchantManualAdjustment: MoneyValue<C>;
    totalCustomerLabelUpchargeFee: MoneyValue<C>;
    /** The net flow adjustment value after adding up all flow adjustments */
    totalFlowAdjustments: MoneyValue<C>;
    /** Sum of all positive (bonus) adjustments */
    totalBonuses: MoneyValue<C>;
    /** Sum of all negative (fee) adjustments */
    totalFees: MoneyValue<C>;
  };
  combinedUnusedUseItOrLoseItBonuses: MoneyValue<C>;
  originalOrderShippingFee: MoneyValue<C>;
  returnShippingFee: ReturnTotals<C>["returnShippingFee"] & {
    amountPaidWithCard: MoneyValue<C>;
    amountDeducted: ReturnTotals<C>["returnShippingFee"]["amountDeducted"] & {
      fromStoreCreditAndRefund: MoneyValue<C>;
    };
  };
  productTotalsList: ProductSummaryTotals<C>[];
}

export const ProductSummaryTotalsSchema_ = z.object({
  id: z.string(),
  type: createEnum(ReturnProductStrategy),
  returnValue: z.object({ amount: z.number(), preTax: z.number() }),
  newOrderVariantValue: z.number(),
  adjustments: z.array(
    z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
  ),
  originalValue: z.object({
    discount: z.number(),
    discountedPrice: z.number(),
    price: z.number(),
    tax: z.number(),
    duty: z.number(),
  }),
});

export const ProductSummaryTotalsSchema: z.ZodType<SerializedProductSummaryTotals> =
  ProductSummaryTotalsSchema_;
export type SerializedProductSummaryTotals = z.infer<
  typeof ProductSummaryTotalsSchema_
>;

export const SerializedNewOrderDiscountLineSchema = z.object({
  label: z.string(),
  kind: z.enum(NewOrderDiscountKinds),
  amount: z.number(),
});
export type SerializedNewOrderDiscountLine = z.infer<
  typeof SerializedNewOrderDiscountLineSchema
>;

/**
 * One exchange line as the calculator priced it: a variant swap or a Shop Now
 * line. `id` is `ProductTotals.id` for a variant exchange and the advanced
 * exchange item's id for a Shop Now line (`<variantId>-<index>` on a draft).
 */
const SerializedExchangeItemTotalsSchema_ = z.object({
  // Optional for backwards compatibility — payloads serialized by a
  // backend predating these fields won't have them.
  id: z
    .string()
    .optional()
    .describe(
      "ProductTotals.id for variant exchanges; the advanced exchange item id for advanced ones.",
    ),
  price: z.number(),
  discount: z.number(),
  discountedPrice: z.number(),
  tax: z
    .number()
    .optional()
    .describe(
      "This item's share of the new order taxes. Sums to newOrderTotals.taxes.total across the list.",
    ),
  discountLines: z
    .array(SerializedNewOrderDiscountLineSchema)
    .optional()
    .describe(
      "Named per-item discount lines when the source persisted attribution; annotation only.",
    ),
  distributedUpsellValue: z
    .number()
    .optional()
    .describe(
      "This item's share of what the customer owes. Only sums to the outstanding balance when advanced exchange items are present to absorb the remainder.",
    ),
  settledBy: z
    .array(z.string())
    .optional()
    .describe(
      "The returned units this line settled against unit for unit, when the allocation engine paired them.",
    ),
});
export const SerializedExchangeItemTotalsSchema: z.ZodType<SerializedExchangeItemTotals> =
  SerializedExchangeItemTotalsSchema_;
export interface SerializedExchangeItemTotals extends z.infer<
  typeof SerializedExchangeItemTotalsSchema_
> {}

export const ReturnSummaryTotalsSchema_ = z.object({
  presentmentCurrency: createEnum(Currency),
  shopCurrency: createEnum(Currency),
  productTotalsList: z.array(ProductSummaryTotalsSchema),
  storeCredit: z.object({
    withoutOriginalOrderShipping: z.number(),
    withOriginalOrderShipping: z.number(),
  }),
  refund: z.object({
    withoutOriginalOrderShipping: z.number(),
    withOriginalOrderShipping: z.number(),
  }),
  outstandingBalanceTotals: z
    .object({
      withDraftBilledShipping: z.number(),
      withoutDraftBilledShipping: z.number(),
      waivedBelowMinimum: z.number().nullish(),
    })
    .nullish(),
  /** @deprecated mirrors `outstandingBalanceTotals.withDraftBilledShipping`. */
  outstandingBalance: z.number(),
  totalPaymentOwed: z.number(),
  // Nullish for backwards compatibility — payloads serialized before the net
  // return amount existed won't have it.
  netReturnAmount: z
    .object({ withoutShipping: z.number(), withShipping: z.number() })
    .nullish(),
  newOrderTotals: z.object({
    totalValue: z.number(),
    preTax: z.number(),
    totalDiscount: z.number(),
    exchangeItemTotalsList: z.array(SerializedExchangeItemTotalsSchema),
    taxes: z.object({
      forVariantExchangeProducts: z.number(),
      forAdvancedExchangeProducts: z.number(),
      total: z.number(),
    }),
    exchangeShippingFee: z.number(),
    exchangeLandedCost: z.number(),
  }),
  originalOrderShippingFee: z.number(),
  returnShippingFee: z.object({
    totalAmount: z.number(),
    labelFeeComponent: z.number().optional(),
    carrierUpgradeFeeComponent: z.number().optional(),
    amountPaidWithCard: z.number(),
    amountDeducted: z.object({
      fromStoreCredit: z.number(),
      fromRefund: z.number(),
      fromStoreCreditAndRefund: z.number(),
      // Optional for backwards compatibility — see comment on
      // ReturnTotals.returnShippingFee.amountDeducted.fromExchange.
      fromExchange: z.number().optional(),
    }),
  }),
  returnValue: z.object({
    amount: z.number(),
    preTaxPreAdjustments: z.number(),
    preReturnLevelAdjustments: z.number().optional(),
    totalTaxes: z.number(),
    totalDiscount: z.number(),
    totalDuty: z.number(),
    merchantDutyDrawback: z.number().optional(),
  }),
  preDiscountShopNowBonus: z.object({
    total: z.number(),
    unused: z.number(),
    used: z.number(),
  }),
  finalSaleCredit: z.number(),
  adjustmentTotals: z.object({
    returnLevelAdjustments: z.array(
      z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
    ),
    totalCustomerLabelUpchargeFee: z.number(),
    totalNetAdjustmentValue: z.number(),
    totalNetMerchantManualAdjustment: z.number(),
    totalFlowAdjustments: z.number().nullish(),
    totalBonuses: z.number().nullish(),
    totalFees: z.number().nullish(),
  }),
});

export const ReturnSummaryTotalsSchema: z.ZodType<SerializedReturnSummaryTotals> =
  ReturnSummaryTotalsSchema_;
export type SerializedReturnSummaryTotals = z.infer<
  typeof ReturnSummaryTotalsSchema_
>;
